import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

import logo from '../../../assets/images/SVG/eduoneLogo.svg';
import ContactAdminModal from '../../common/ContactAdminModal/ContactAdminModal';
import authService from '../../../infrastructure/services/authService';

const OtpVerify = () => {
    const navigate = useNavigate();
    const [otp, setOtp] = useState(new Array(6).fill(''));
    const [error, setError] = useState('');
    const [verifying, setVerifying] = useState(false);
    const [resending, setResending] = useState(false);
    const [isContactOpen, setIsContactOpen] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '' });
    const [activeInput, setActiveInput] = useState(0);

    const inputRefs = useRef([]);
    const resetTarget = sessionStorage.getItem('resetTarget');
    const resetType = sessionStorage.getItem('resetType') || 'email';
    const resetUserId = sessionStorage.getItem('resetUserId');

    // Redirect if resetTarget is missing
    useEffect(() => {
        if (!resetTarget) {
            navigate('/forgot-password/email', { replace: true });
        }
    }, [resetTarget, navigate]);

    // Prevent back navigation by pushing current state to history
    useEffect(() => {
        if (!resetTarget) return;

        window.history.pushState(null, document.title, window.location.href);

        const handlePopState = (e) => {
            window.history.pushState(null, document.title, window.location.href);
            setError('Please complete the OTP verification or click Cancel to return.');
        };

        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [resetTarget]);

    // Focus on first input on mount
    useEffect(() => {
        if (resetTarget && inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
    }, [resetTarget]);

    const handleCancelReset = () => {
        sessionStorage.removeItem('resetTarget');
        sessionStorage.removeItem('resetType');
        sessionStorage.removeItem('resetUserId');
        sessionStorage.removeItem('resetOtp');
        navigate('/', { replace: true });
    };

    if (!resetTarget) {
        return null;
    }

    const handleChange = (e, index) => {
        const val = e.target.value;
        if (isNaN(Number(val))) return; // allow numbers only

        const newOtp = [...otp];
        // Take only the last typed character
        newOtp[index] = val.substring(val.length - 1);
        setOtp(newOtp);

        if (error) setError('');

        // Move focus to next input if filled
        if (val && index < 5) {
            inputRefs.current[index + 1].focus();
            setActiveInput(index + 1);
        }
    };

    const handleKeyDown = (e, index) => {
        // Move focus to previous input on backspace
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1].focus();
            setActiveInput(index - 1);
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pasteData = e.clipboardData.getData('text').trim();
        if (pasteData.length !== 6 || isNaN(Number(pasteData))) return;

        const pasteDigits = pasteData.split('');
        setOtp(pasteDigits);
        if (error) setError('');
        inputRefs.current[5].focus();
        setActiveInput(5);
    };

    const handleResend = async () => {
        if (!resetTarget || resending) return;
        setResending(true);
        try {
            await authService.forgotPasswordInitiate(resetTarget, resetUserId);
            setToast({ open: true, message: 'A new OTP has been dispatched to ' + resetTarget });
            setOtp(new Array(6).fill(''));
            setError('');
            if (inputRefs.current[0]) {
                inputRefs.current[0].focus();
                setActiveInput(0);
            }
        } catch (err) {
            setError(err?.message || 'Failed to resend OTP');
        } finally {
            setResending(false);
        }
    };

    const handleVerify = async (e) => {
        e.preventDefault();
        const otpCode = otp.join('');

        if (otpCode.length < 6) {
            setError('Please enter all 6 digits of the OTP');
            return;
        }

        setError('');
        setVerifying(true);
        try {
            await authService.forgotPasswordVerify(resetTarget, otpCode, resetUserId);
            sessionStorage.setItem('resetOtp', otpCode);
            navigate('/forgot-password/new-password');
        } catch (err) {
            setError(err?.message || 'Invalid OTP');
        } finally {
            setVerifying(false);
        }
    };

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: '480px',
                my: 'auto',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                justifyContent: 'center',
                fontFamily: "'Poppins', sans-serif"
            }}
        >
            <Snackbar
                open={toast.open}
                autoHideDuration={3000}
                onClose={() => setToast({ open: false, message: '' })}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{
                    '& .MuiPaper-root': {
                        borderRadius: 12,
                        boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
                    }
                }}
            >
                <Alert
                    onClose={() => setToast({ open: false, message: '' })}
                    severity="success"
                    variant="filled"
                    sx={{
                        width: '100%',
                        borderRadius: '12px',
                        bgcolor: '#ff502d',
                        color: '#fff'
                    }}
                >
                    {toast.message}
                </Alert>
            </Snackbar>

            {/* Logo */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mb: { xs: 4, md: 5 }
                }}
            >
                <Box
                    component="img"
                    src={logo}
                    alt="EduOne Logo"
                    sx={{
                        height: '45px',
                        width: 'auto'
                    }}
                />
            </Box>

            {/* Header Text */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography
                    variant="h2"
                    sx={{
                        fontSize: { xs: '26px', sm: '28px' },
                        fontWeight: 700,
                        color: '#231F20',
                        mb: 1.5,
                        fontFamily: "'Poppins', sans-serif"
                    }}
                >
                    OTP Sent to Your {resetType === 'mobile' ? 'Mobile' : 'Email'}
                </Typography>
                <Typography
                    sx={{
                        fontSize: '14px',
                        color: '#9B9B9B',
                        lineHeight: 1.6,
                        maxWidth: '400px',
                        mx: 'auto',
                        fontFamily: "'Poppins', sans-serif"
                    }}
                >
                    We've sent a one-time password to your registered {resetType === 'mobile' ? 'mobile number' : 'email address'}. Enter the code below to continue resetting your password.
                </Typography>
            </Box>

            {/* Form */}
            <Box
                component="form"
                onSubmit={handleVerify}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {/* OTP Inputs grid */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: { xs: 1, sm: 1.5 },
                            width: '100%'
                        }}
                    >
                        {otp.map((digit, idx) => (
                            <Box
                                key={idx}
                                component="input"
                                type="text"
                                maxLength="1"
                                value={digit}
                                ref={(el) => (inputRefs.current[idx] = el)}
                                onChange={(e) => handleChange(e, idx)}
                                onKeyDown={(e) => handleKeyDown(e, idx)}
                                onPaste={handlePaste}
                                onFocus={() => setActiveInput(idx)}
                                sx={{
                                    width: { xs: '42px', sm: '56px' },
                                    height: { xs: '48px', sm: '56px' },
                                    borderRadius: '16px',
                                    border: '1.5px solid',
                                    borderColor: error ? '#e74c3c' : (activeInput === idx ? '#0ea5e9' : '#cbd5e1'),
                                    backgroundColor: '#ffffff',
                                    textAlign: 'center',
                                    fontSize: { xs: '18px', sm: '20px' },
                                    fontWeight: 700,
                                    color: '#231F20',
                                    outline: 'none',
                                    fontFamily: "'Poppins', sans-serif",
                                    transition: 'all 0.2s ease',
                                    boxShadow: activeInput === idx ? '0 0 0 3px rgba(14, 165, 233, 0.1)' : 'none',
                                    '&:focus': {
                                        borderColor: '#0ea5e9'
                                    }
                                }}
                            />
                        ))}
                    </Box>

                    {error && (
                        <Typography
                            component="span"
                            sx={{
                                color: '#e74c3c',
                                fontSize: '14px',
                                mt: '4px',
                                fontFamily: "'Poppins', sans-serif"
                            }}
                        >
                            {error}
                        </Typography>
                    )}
                </Box>

                {/* Resend Link Section */}
                <Box>
                    <Typography
                        sx={{
                            fontSize: '14px',
                            color: '#475569',
                            fontFamily: "'Poppins', sans-serif"
                        }}
                    >
                        Didn't receive OTP?{' '}
                        <Box
                            component="span"
                            onClick={resending ? null : handleResend}
                            sx={{
                                color: resending ? '#94a3b8' : '#ff502d',
                                fontWeight: 700,
                                cursor: resending ? 'default' : 'pointer',
                                transition: 'color 0.2s',
                                '&:hover': {
                                    color: resending ? '#94a3b8' : '#e04324'
                                }
                            }}
                        >
                            {resending ? 'Resending...' : 'Resend OTP'}
                        </Box>
                    </Typography>
                </Box>

                {/* Verify Button */}
                <Button
                    type="submit"
                    variant="contained"
                    disabled={verifying || otp.join('').length < 6}
                    sx={{
                        width: '100%',
                        background: '#ff502d',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '16px',
                        py: 2,
                        px: 3,
                        fontSize: '16px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        textTransform: 'none',
                        boxShadow: '0 8px 20px -6px rgba(255, 80, 45, 0.4)',
                        fontFamily: "'Poppins', sans-serif",
                        transition: 'all 0.25s ease',
                        '&:hover:not(:disabled)': {
                            background: '#e04324',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 12px 24px -6px rgba(255, 80, 45, 0.5)'
                        },
                        '&:disabled': {
                            background: '#ff502d',
                            opacity: 0.6,
                            color: '#fff',
                            boxShadow: 'none',
                            cursor: 'not-allowed'
                        }
                    }}
                >
                    {verifying ? 'Verifying...' : 'Verify'}
                </Button>

                {/* Cancel Link */}
                <Box sx={{ textAlign: 'center', mt: 1 }}>
                    <Typography
                        onClick={handleCancelReset}
                        sx={{
                            fontSize: '14px',
                            color: '#94a3b8',
                            fontFamily: "'Poppins', sans-serif",
                            cursor: 'pointer',
                            transition: 'color 0.2s',
                            textDecoration: 'underline',
                            '&:hover': {
                                color: '#ff502d'
                            }
                        }}
                    >
                        Cancel reset and return to login
                    </Typography>
                </Box>
            </Box>

            {/* Support footer */}
            <Box sx={{ textAlign: 'center', mt: 5, mb: 1 }}>
                <Typography
                    sx={{
                        fontSize: '14px',
                        color: '#475569',
                        fontFamily: "'Poppins', sans-serif"
                    }}
                >
                    Need help to reset password ?{' '}
                    <Box
                        component="span"
                        onClick={() => setIsContactOpen(true)}
                        sx={{
                            color: '#ff502d',
                            fontWeight: 600,
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            '&:hover': {
                                color: '#e04324'
                            }
                        }}
                    >
                        Contact us
                    </Box>
                </Typography>
            </Box>

            {/* Reusable ContactAdminModal */}
            <ContactAdminModal
                isOpen={isContactOpen}
                onClose={() => setIsContactOpen(false)}
            />
        </Box>
    );
};

export default OtpVerify;
